export type IsoDate = string

export type LanguageCode = string

export type LocalizedText = Readonly<Record<LanguageCode, string>>

export interface MoneyAmount {
  readonly amount: number
  readonly currency: string
}

export interface DateRange {
  readonly start: IsoDate
  readonly end: IsoDate
}

export enum PromotionSegment {
  BPS = 'bps',
  CPS = 'cps',
  Display = 'display',
  Poly = 'poly',
  Other = 'other',
  OPS = 'ops',
  HPS = 'hps',
}

export enum SortOrder {
  StockAsc = 's_asc',
  StockDesc = 's_desc',
  StandardDiscountAsc = 'sd_asc',
  StandardDiscountDesc = 'sd_desc',
  AdditionalDiscountAsc = 'ad_asc',
  AdditionalDiscountDesc = 'ad_desc',
  DealEndDateAsc = 'dd_asc',
  DealEndDateDesc = 'dd_desc',
}

export type PromotionKind = 'deal' | 'hot_offer' | 'end_customer'

/**
 * The same three codes the search takes as `promotionType`. Kept as a runtime
 * list because free-form input reaches that filter: the service answers an
 * unknown value with a 400, so it must never leave the client.
 */
export const PROMOTION_KINDS: readonly PromotionKind[] = ['deal', 'hot_offer', 'end_customer']

export const isPromotionKind = (value: string): value is PromotionKind =>
  PROMOTION_KINDS.some((kind) => kind === value)

export type OfferTypeMarker = PromotionKind

export type DealLineType = 'BD' | 'PN'

/**
 * A partner eligible for a deal, and the card of its «Deal distributors»
 * section: the feed knows nothing about a deal's distributors beyond these.
 */
export interface Reseller {
  readonly name: string
  /**
   * Partner Profile Identifier. `null` is the data as it is — the wide FDL
   * export carries the column and the sample does not, and the live feed sends
   * a literal `?` when it has none. A card without a PPID is still drawn.
   */
  readonly ppid: string | null
}

/**
 * Who holds stock of a SKU and how much, as of the day the warehouse reported.
 * Belongs to the SKU rather than to an offer: stock is reported per SKU, and
 * the same section is what a hot offer or an end customer promo shows.
 */
export interface StockDistributor {
  readonly name: string
  readonly ppid: string | null
  readonly stock: number
  /** «Stock at date». A quantity with no date must not be shown at all. */
  readonly stockDate: IsoDate
}

export interface BundleItem {
  readonly productId: string
  readonly productLineId: string
  readonly description: string
  readonly position: number
  readonly isMainProduct: boolean
}
